import { PDFDocument } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";

import { SplitPageError } from "@/constants/splitPage";
import { FileSplitException } from "@/types/jobFileUpload/FileSplitException";

import Logger from "../logger";
import { isEncryptedPdfError } from "./isEncryptedPdfError";
import { splitPageErrorHandler } from "./splitPageErrorHandler";

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/legacy/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

// This number is used to scale the page images to a higher resolution.
const RENDER_SCALE = 3.0;

interface LoadPdfDocumentOptions {
  returnPageCount?: boolean;
  pageRange?: [number, number];
}

/**
 * Loads a PDF document using a blank password
 *
 * @param file - The PDF file to load
 * @returns The loaded PDF.js document
 */
const loadPdfWithBlankPassword = async (file: File): Promise<pdfjsLib.PDFDocumentProxy> => {
  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({
    data: new Uint8Array(arrayBuffer),
    password: "",
  });

  try {
    const pdfDocument = await loadingTask.promise;
    return pdfDocument;
  } catch (passwordError) {
    if (isPdfPasswordProtectedError(passwordError)) {
      throw new FileSplitException(
        SplitPageError.NON_BLANK_PASSWORD_PDF_LOAD_FAILED,
        "This PDF is password-protected with a non-empty password and cannot be processed."
      );
    }

    throw splitPageErrorHandler(passwordError);
  }
};

/**
 * Creates a high-quality PDF document by rendering each page of the source document
 *
 * @param pdfDocument - The source PDF.js document
 * @param pageRange - The range of pages to load
 * @returns A new PDFDocument instance with high-quality rendered pages
 */
const createHighQualityPdfDocument = async (
  pdfDocument: pdfjsLib.PDFDocumentProxy,
  pageRange: [number, number]
): Promise<PDFDocument> => {
  // Create a new PDF document
  const newPdfDoc = await PDFDocument.create();

  // Process each page in the document
  const startIndex = pageRange[0] - 1;
  const endIndex = pageRange[1];
  for (let pageIndex = startIndex; pageIndex < endIndex; pageIndex++) {
    try {
      await processAndAddPage(pdfDocument, pageIndex, newPdfDoc);
    } catch (pageError) {
      addErrorPage(newPdfDoc, pageIndex, pageError);
    }
  }

  return newPdfDoc;
};

/**
 * Processes a single page from the source document and adds it to the target document
 *
 * @param pdfDocument - The source PDF.js document
 * @param pageIndex - The 0-based index of the page to process
 * @param targetDocument - The target PDF document to add the page to
 */
const processAndAddPage = async (
  pdfDocument: pdfjsLib.PDFDocumentProxy,
  pageIndex: number,
  targetDocument: PDFDocument
): Promise<void> => {
  const pageNumber = pageIndex + 1;
  const page = await pdfDocument.getPage(pageNumber);
  // Render the page to a high-quality canvas
  const { canvas, viewport, scale } = await renderPageToCanvas(page);

  // Convert the canvas to a PDF page
  await addCanvasToDocument(canvas, viewport, scale, targetDocument);
};

/**
 * Renders a PDF.js page to a high-quality canvas
 *
 * @param page - The PDF.js page to render
 * @returns The canvas, viewport, and scale used for rendering
 */
const renderPageToCanvas = async (
  page: pdfjsLib.PDFPageProxy
): Promise<{
  canvas: HTMLCanvasElement;
  viewport: pdfjsLib.PageViewport;
  scale: number;
}> => {
  const scale = RENDER_SCALE;
  const viewport = page.getViewport({ scale });

  // Create and configure canvas
  const canvas = document.createElement("canvas");
  canvas.width = viewport.width;
  canvas.height = viewport.height;

  // Get and configure rendering context
  const context = canvas.getContext("2d", {
    alpha: false,
    desynchronized: false,
    willReadFrequently: true,
  });

  if (!context) {
    throw new Error("Could not get canvas context");
  }

  // Configure for high-quality rendering
  context.imageSmoothingEnabled = false;
  canvas.style.imageRendering = "pixelated";

  // Set white background
  context.fillStyle = "white";
  context.fillRect(0, 0, canvas.width, canvas.height);

  // Render with high quality
  await page.render({
    canvasContext: context,
    canvas: canvas,
    viewport: viewport,
    intent: "print",
  }).promise;

  return { canvas, viewport, scale };
};

/**
 * Adds a canvas as a new page in the target PDF document
 *
 * @param canvas - The canvas with the rendered page
 * @param viewport - The viewport used for rendering
 * @param scale - The scale used for rendering
 * @param targetDocument - The target PDF document
 */
const addCanvasToDocument = async (
  canvas: HTMLCanvasElement,
  viewport: pdfjsLib.PageViewport,
  scale: number,
  targetDocument: PDFDocument
): Promise<void> => {
  /*
   * Convert the canvas to a high-quality PNG image with maximum quality
   * PNG provides better compression than JPEG for photographic content while maintaining quality
   */
  const imageData = canvas.toDataURL("image/png", 1.0);

  // Embed the image in the PDF
  const image = await targetDocument.embedPng(imageData);

  // Add a page with the same dimensions but scaled back down
  const newPage = targetDocument.addPage([viewport.width / scale, viewport.height / scale]);

  // Draw the image on the page, scaling it to fit
  newPage.drawImage(image, {
    x: 0,
    y: 0,
    width: viewport.width / scale,
    height: viewport.height / scale,
  });
};

/**
 * Adds an error page to the document when page processing fails
 *
 * @param targetDocument - The target PDF document
 * @param pageIndex - The index of the page that failed
 * @param error - The error that occurred
 */
const addErrorPage = (targetDocument: PDFDocument, pageIndex: number, error: unknown): void => {
  Logger.error("error", { data: `Error processing page ${pageIndex + 1}: ${error}` });

  // Add an empty page with error message
  const newPage = targetDocument.addPage();
  const { height } = newPage.getSize();

  newPage.drawText(`Error rendering page ${pageIndex + 1}`, {
    x: 50,
    y: height - 50,
    size: 20,
  });
};

const isPdfPasswordProtectedError = (error: unknown): boolean => {
  return error instanceof Error && error.name === "PasswordException";
};

/**
 * Validates that the PDF document contains pages
 *
 * @param pageCount - The number of pages in the PDF document
 * @throws {FileSplitException} If the document contains no pages
 */
const validatePdfPageCount = (pageCount: number): void => {
  if (pageCount === 0) {
    throw new FileSplitException(SplitPageError.EMPTY_DOCUMENT, "PDF document contains no pages");
  }
};

/**
 * Handles errors that occur during PDF loading
 *
 * @param error - The error that occurred
 * @param file - The original PDF file
 * @param returnPageCount - Whether this function is being called to get page count
 * @returns The loaded PDF document if recovery is possible
 * @throws {FileSplitException} If the document cannot be loaded
 */
const handlePdfLoadError = async (error: unknown, file: File, returnPageCount: boolean): Promise<PDFDocument> => {
  // When called from getPdfPageCount, propagate all errors
  if (returnPageCount) {
    throw error;
  }

  // For encrypted PDFs, try the blank password handler
  if (isEncryptedPdfError(error)) {
    return loadPdfDocumentBlankPassword<PDFDocument>(file);
  }

  // For all other errors, use the standard error handler
  throw splitPageErrorHandler(error);
};

/**
 * Attempts to load a PDF using the standard pdf-lib loader
 *
 * @param file - The PDF file to load
 * @returns The loaded PDF document
 * @throws {FileSplitException} If the document is empty
 */
const loadStandardPdf = async (file: File): Promise<PDFDocument> => {
  const arrayBuffer = await file.arrayBuffer();
  const pdfDoc = await PDFDocument.load(arrayBuffer);

  validatePdfPageCount(pdfDoc.getPageCount());
  return pdfDoc;
};

/**
 * Loads a PDF document with a blank password and renders it as a new PDF document
 * with high-quality page images.
 *
 * @param file - The PDF file to load
 * @param options - The options for the loadPdfDocumentBlankPassword function
 * @returns Either a PDFDocument instance or the page count as a number
 */
const loadPdfDocumentBlankPassword = async <T extends PDFDocument | number>(
  file: File,
  options: LoadPdfDocumentOptions = {}
): Promise<T> => {
  const pdfDocument = await loadPdfWithBlankPassword(file);
  validatePdfPageCount(pdfDocument.numPages);

  const { returnPageCount = false, pageRange = [1, pdfDocument.numPages] } = options;
  return returnPageCount
    ? (pdfDocument.numPages as T)
    : ((await createHighQualityPdfDocument(pdfDocument, pageRange)) as T);
};

/**
 * Loads a PDF document from a file, handling various error cases
 *
 * This function attempts to load a PDF using pdf-lib. If the document is encrypted,
 * it falls back to using the blank password handler.
 *
 * @param file - The PDF file to load
 * @param returnPageCount - Whether this function is being called to get page count (affects error handling)
 * @returns The loaded PDF document
 * @throws {FileSplitException} If the document cannot be loaded or is empty
 */

const loadPdfDocument = async (file: File, options: LoadPdfDocumentOptions = {}): Promise<PDFDocument> => {
  const { returnPageCount = false } = options;
  try {
    return await loadStandardPdf(file);
  } catch (error) {
    return handlePdfLoadError(error, file, returnPageCount);
  }
};

export { loadPdfDocument, loadPdfDocumentBlankPassword };
